import React from "react";
import type { Project } from "../../../api/useProjects";
import SidePanelLayout from "@ui/SidePanel/Layout";
import { useOutletContext } from "react-router";
import { useSettings } from "../../../api/useSettings";
import Secondary from "@ui/Header/Secondary";
import { toPath, useIsCurrentRouteMatching } from "../../../router/helpers/path";
import NavV2 from "@ui/NavV2";
import Tab from "@ui/NavV2/Tab";
import LoadingBoundary from "@ui/LoadingBoundary";
import { css } from "@linaria/core";
import { spacings } from "@ui/Theme";
import t from "../../../i18n";
import { useSetups } from "../../../api/useSetups";

const Exploration = ({ children }: { children: React.ReactNode }) => {
  const settings = useSettings();

  const project = useOutletContext<Project>();
  if (!project) throw new Error("no project");

  const setups = useSetups(project.id);
  const hasSetup = setups.length > 0;

  const isDiagnosisPage = useIsCurrentRouteMatching("/projects/:projectId/diagnosis");
  const isSymptomsPage = useIsCurrentRouteMatching("/projects/:projectId/symptoms");
  const isMeasuresPage = useIsCurrentRouteMatching("/projects/:projectId/measures");

  if (settings.NEW_NAVIGATION_ENABLED === false) {
    return children;
  }

  return (
    <SidePanelLayout className={panelMain}>
      <Secondary
        className={secondary}
        nav={
          <>
            <NavV2>
              <Tab
                data-section-id="measures"
                to={toPath("/projects/:projectId/measures", {
                  projectId: project.id,
                })}
                active={isMeasuresPage}
              >
                {t("project.measures.title")}
              </Tab>
              {hasSetup && (
                <Tab
                  data-section-id="symptoms"
                  to={toPath("/projects/:projectId/symptoms", {
                    projectId: project.id,
                  })}
                  active={isSymptomsPage}
                >
                  {t("project.symptoms.title")}
                </Tab>
              )}
              {hasSetup && (
                <Tab
                  data-section-id="diagnosis"
                  to={toPath("/projects/:projectId/diagnosis", { projectId: project.id })}
                  active={isDiagnosisPage}
                >
                  {t("project.document.title.diagnosis")}
                </Tab>
              )}
            </NavV2>
          </>
        }
      />
      <LoadingBoundary>
        <SidePanelLayout className={content}>{children}</SidePanelLayout>
      </LoadingBoundary>
    </SidePanelLayout>
  );
};

export default React.memo(Exploration);

//#region CSS
const panelMain = css`
  gap: 0;
  padding: 0 !important;
`;
const content = css`
  gap: 0;
  padding: 0 ${spacings.xxl} ${spacings.l} ${spacings.xxl} !important;
`;
const secondary = css`
  padding: 0 ${spacings.xxl};
`;
//#endregion CSS
